import { useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7lnjitp9sllopf7hk0";

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSEO({
    title: "关于我们 - 小泽玛利亚明星资讯站",
    description:
      "了解小泽玛利亚明星资讯站的定位、服务内容与联系方式。本站专注提供她的个人资料、最新新闻与影视作品资讯。",
    keywords: "小泽玛利亚,关于我们,资讯站,联系方式,粉丝平台",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str === "") return;
      payload.append(key, str);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="animate-fade-up mx-auto max-w-content px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <div className="mt-5 mb-8">
        <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
          About Us
        </span>
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mt-1.5">
          关于本站
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2 flex flex-col gap-8">
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-compass-3-line text-primary-600"></i>
              网站定位
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
              小泽玛利亚明星资讯站是一个面向粉丝群体、娱乐资讯爱好者及潜在新粉丝的垂直资讯平台。我们聚焦于小泽玛利亚的个人资料、最新新闻、影视作品与相关动态，希望让每一位来访的朋友都能快速获取准确、有条理的权威信息。
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              网站以专题报道与资讯流结合的方式呈现内容，兼顾可读性与视觉吸引力。所有资讯、作品与资料均为演示内容，用于展示站点的信息组织方式与浏览体验。
            </p>
          </section>

          <section className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-team-line text-accent-600"></i>
              网站团队声明
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
              本站由一支热爱娱乐资讯内容的小团队独立维护。我们坚持“真实、清晰、及时”的内容原则，努力为粉丝提供良好的阅读体验。
            </p>
            <ul className="flex flex-col gap-3">
              {[
                "内容整理：负责资讯采集、作品归档与资料核实",
                "视觉设计：负责站点的界面呈现与阅读体验优化",
                "用户支持：负责粉丝反馈与留言的处理与回复",
              ].map((text) => (
                <li key={text} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
                  {text}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-chat-smile-2-line text-primary-600"></i>
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-4">
                <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">合作与投稿邮箱</p>
                  <p className="text-sm text-foreground-900 truncate">contact@mariaozawa-fan.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-4">
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                  <i className="ri-time-line"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">留言处理时间</p>
                  <p className="text-sm text-foreground-900">工作日 48 小时内回复</p>
                </div>
              </div>
            </div>

            <form
              id="about-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-primary-50 border border-primary-100 rounded-lg p-5 md:p-6"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                给我们留言
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入称呼"
                    className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-1.5">
                  留言类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                >
                  <option value="内容反馈">内容反馈</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="投稿建议">投稿建议</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请写下你的留言，最多 500 字"
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                ></textarea>
              </div>

              <input
                className="field-contact-verify"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </>
                  )}
                </button>
                {status === "success" && (
                  <span className="text-sm text-primary-700 flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-fill"></i>
                    留言提交成功，感谢你的反馈！
                  </span>
                )}
                {status === "error" && formError && (
                  <span className="text-sm text-accent-700 flex items-center gap-1.5">
                    <i className="ri-error-warning-fill"></i>
                    {formError}
                  </span>
                )}
              </div>
            </form>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-600"></i>
              版权声明
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              本站为粉丝向资讯平台，所有内容均为演示资料，仅供娱乐资讯展示与学习交流使用，不用于任何商业用途。如涉及相关问题，可通过上方邮箱与我们联系。
            </p>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 flex flex-col gap-5">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-map-2-line text-primary-600"></i>
                站点信息
              </h3>
              <ul className="flex flex-col gap-3 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-pages-line text-primary-500 mt-0.5"></i>
                  类型：明星粉丝资讯站
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-earth-line text-primary-500 mt-0.5"></i>
                  语言：简体中文
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-device-line text-primary-500 mt-0.5"></i>
                  适配：电脑 / 手机
                </li>
              </ul>
            </div>

            <div className="bg-primary-50 border border-primary-100 rounded-lg p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                快速开始
              </h3>
              <div className="flex flex-col gap-2.5">
                <Link
                  to="/category/news"
                  className="inline-flex items-center justify-between px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  浏览最新新闻
                  <i className="ri-arrow-right-line text-primary-600"></i>
                </Link>
                <Link
                  to="/works"
                  className="inline-flex items-center justify-between px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  查看影视作品
                  <i className="ri-arrow-right-line text-primary-600"></i>
                </Link>
                <Link
                  to="/profile"
                  className="inline-flex items-center justify-between px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  查看个人资料
                  <i className="ri-arrow-right-line text-primary-600"></i>
                </Link>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}